import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X as XIcon, ArrowDown, ArrowUp, ArrowUUpLeft, ArrowsLeftRight, Package, Barcode as BarcodeIcon } from "@phosphor-icons/react";

const TYPE_ICON = { entry: ArrowDown, exit: ArrowUp, return: ArrowUUpLeft, transfer: ArrowsLeftRight, scarico_commessa: ArrowUp };
const TYPE_LABEL = {
  entry: "Entrata", exit: "Uscita", return: "Reso",
  transfer: "Trasferim.", scarico_commessa: "Scarico Commessa",
};
const TYPE_STYLES = {
  entry: "border-emerald-300 text-emerald-800 bg-emerald-50",
  exit: "border-red-300 text-red-800 bg-red-50",
  return: "border-amber-300 text-amber-800 bg-amber-50",
  transfer: "border-blue-300 text-blue-800 bg-blue-50",
  scarico_commessa: "border-purple-300 text-purple-800 bg-purple-50",
};

/**
 * Modal that shows the movement history for a product (mode="product")
 * or for a specific serial (mode="serial").
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   mode: "product" | "serial"
 *   productId?: string
 *   productName?: string
 *   productSku?: string
 *   serial?: string
 */
export default function MovementHistoryModal({ open, onClose, mode, productId, productName, productSku, serial }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const params = {};
    if (mode === "product" && productId) params.product_id = productId;
    if (mode === "serial" && serial) params.serial = serial;
    setLoading(true); setRows([]);
    api.get("/movements", { params })
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, mode, productId, serial]);

  if (!open) return null;

  const title = mode === "serial" ? "Storico seriale" : "Storico prodotto";
  const subtitle = mode === "serial"
    ? serial
    : (productName ? `${productName}${productSku ? ` · ${productSku}` : ""}` : "");
  const HeaderIcon = mode === "serial" ? BarcodeIcon : Package;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 md:p-4">
      <div className="bg-white border border-neutral-200 shadow-xl w-full max-w-3xl max-h-[95vh] flex flex-col" data-testid="movement-history-modal">
        <div className="p-4 md:p-6 border-b border-neutral-200 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="label-eyebrow mb-1">{title}</div>
            <h2 className="font-display font-bold text-lg md:text-xl tracking-tight flex items-center gap-2 min-w-0">
              <HeaderIcon size={20} weight="bold" className="text-[#002FA7] shrink-0" />
              <span className="truncate">{subtitle}</span>
            </h2>
          </div>
          <button data-testid="close-history" onClick={onClose} className="p-1.5 hover:bg-neutral-100 transition-colors shrink-0">
            <XIcon size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {loading && <div className="text-sm text-neutral-500 py-8 text-center">Caricamento storico...</div>}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-neutral-500 py-8 text-center">Nessun movimento trovato.</div>
          )}
          {!loading && rows.length > 0 && (
            <ol className="space-y-2">
              {rows.map((m) => {
                const Icon = TYPE_ICON[m.type] || ArrowDown;
                return (
                  <li key={m.id} className="border border-neutral-200 p-3">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] border font-semibold uppercase tracking-wider ${TYPE_STYLES[m.type]}`}>
                        <Icon size={11} weight="bold" /> {TYPE_LABEL[m.type] || m.type}
                      </span>
                      <span className="text-[11px] font-mono text-neutral-500 shrink-0">
                        {new Date(m.created_at).toLocaleString("it-IT")}
                      </span>
                    </div>
                    <div className="text-xs text-neutral-700">
                      <span className="text-neutral-500">{m.from_warehouse_name || "—"}</span>
                      <span className="mx-1 text-neutral-400">→</span>
                      <span className="font-medium">{m.to_warehouse_name || "—"}</span>
                      {typeof m.quantity === "number" && (
                        <span className="ml-2 text-neutral-500">· Qtà {m.quantity}</span>
                      )}
                    </div>
                    {mode === "product" && m.product_is_serialized && m.serial_numbers?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {m.serial_numbers.slice(0, 6).map((sn) => (
                          <span key={sn} className="inline-block px-1.5 py-0.5 bg-neutral-100 border border-neutral-200 font-mono text-[10px]">{sn}</span>
                        ))}
                        {m.serial_numbers.length > 6 && (
                          <span className="text-[10px] text-neutral-500">+{m.serial_numbers.length - 6}</span>
                        )}
                      </div>
                    )}
                    {m.customer_name && <div className="mt-1 text-xs text-neutral-700">👤 {m.customer_name}</div>}
                    {m.commessa_name && <div className="mt-1 text-xs text-neutral-700">📍 {m.commessa_name}</div>}
                    {m.reference && <div className="mt-1 text-xs text-neutral-500">{m.type === "entry" ? "DDT" : "Rif"}: {m.reference}</div>}
                    {m.notes && <div className="mt-1 text-xs text-neutral-500 italic">Note: {m.notes}</div>}
                    <div className="mt-1 text-[10px] text-neutral-400 font-mono">{m.created_by}</div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="p-4 border-t border-neutral-200 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 transition-colors">
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
